import { useEffect, useState } from "react";
import { Kpi } from "@features/admin/kpi/types/kpi";
import { fetchKpiAsync, fetchAssignedKpiAsync } from "@features/admin/kpi/services/kpiSvc";
import { KpiQueryParams } from "@features/admin/kpi/types/kpiQueryParams";
import { normalizeAllForList } from "@core/utils/listQuery";


/**
 * React hook that fetches and manages KPI (Key Performance Indicator) data with pagination support.
 *
 * This hook wraps calls to an underlying `fetchKpiAsync` function and exposes pagination metadata,
 * the fetched list of KPI items, a loading flag, and helper methods to reload or fetch KPIs by activity id.
 * It keeps internal state for pagination fields (current page, first/last page URLs, links, etc.) and updates
 * them whenever relevant `params` or `options.enabled` change.
 *
 * The hook triggers an initial load and subsequent reloads in a useEffect when any of the following change:
 * `params.nationId`, `params.page`, `params.all`, `params.activityId`, or `options.enabled`.
 *
 * Behavior notes:
 * - While performing network operations the `loading` flag is set to `true`. It is reset to `false` after the
 *   operation completes (whether successful or failed).
 * - If `options.enabled` is falsy, `reload()` will guard and return early without fetching.
 * - `fetchKpiByActivityId(activityId)` performs a fetch filtered by the provided activity id and returns
 *   the data array; it sets `loading` during the fetch but does not mutate the hook's stored pagination
 *   metadata or `data` state (it only returns the fetched items).
 *
 * @param params - Query parameters for fetching KPIs (e.g. `nationId`, `page`, `all`, `activityId`, etc.).
 * @param options - Optional configuration for the hook.
 * @param options.enabled - If false, automatic loads and explicit `reload()` calls are guarded and will not perform network requests. Defaults to `true`.
 *
 * @returns An object containing:
 * - `current_page: number` - Current page number (defaults to 1).
 * - `data: Kpi[]` - Array of fetched KPI items.
 * - `first_page_url?: string` - URL for the first page, if provided by the API.
 * - `from?: number` - Index of the first item in the current page (1-based), if provided.
 * - `last_page?: number` - Last page number, if provided.
 * - `last_page_url?: string` - URL for the last page, if provided by the API.
 * - `links?: { url: string | null; label: string; active: boolean }[]` - Pagination link items, if provided.
 * - `next_page_url?: string | null` - URL for the next page, if any.
 * - `path?: string` - Base API path, if provided by the API.
 * - `per_page?: number` - Items per page, if provided.
 * - `prev_page_url?: string | null` - URL for the previous page, if any.
 * - `to?: number` - Index of the last item in the current page (1-based), if provided.
 * - `total: number` - Total number of items across all pages (defaults to 0).
 * - `loading: boolean` - True while a fetch is in progress.
 * - `reload: () => Promise<void>` - Async function to (re)fetch data using the current `params`. Respects `options.enabled`.
 * - `fetchKpiByActivityId: (activityId: number) => Promise<Kpi[]>` - Async helper to fetch KPI items filtered by `activityId`. Returns the fetched items array.
 *
 */
export function useKpi(params: KpiQueryParams, options: { enabled?: boolean } = { enabled: true }) {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<Kpi[]>([]);
    const [first_page_url, setFirstPageUrl] = useState<string | undefined>(undefined);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [last_page_url, setLastPageUrl] = useState<string | undefined>(undefined);
    const [links, setLinks] = useState<{ url: string | null; label: string; active: boolean }[] | undefined>(undefined);
    const [next_page_url, setNextPageUrl] = useState<string | null | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [prev_page_url, setPrevPageUrl] = useState<string | null | undefined>(undefined);
    const [to, setTo] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // create a function to reload the data
    const reload = async () => {
        setLoading(true);

        if (!options?.enabled) return;   // guard until ready

        try {
            const { nationId, ...rawFetchParams } = params;
            const fetchParams = normalizeAllForList(rawFetchParams, [
                "freetext",
                "activityId",
                "kpiName",
                "activityName",
                "description",
                "dataTypeName",
                "unitName",
                "kpiId",
                "dataTypeId",
                "unitId",
                "isActive",
                "entryMode",
            ]);
            const response = await fetchKpiAsync(nationId, fetchParams);
            if (response) {
                setCurrentPage(response.getData()?.current_page || 1);
                setData(response.getData()?.data || []);
                setFirstPageUrl(response.getData()?.first_page_url);
                setFrom(response.getData()?.from);
                setLastPage(response.getData()?.last_page);
                setLastPageUrl(response.getData()?.last_page_url);
                setLinks(response.getData()?.links);
                setNextPageUrl(response.getData()?.next_page_url);
                setPath(response.getData()?.path);
                setPerPage(response.getData()?.per_page);
                setPrevPageUrl(response.getData()?.prev_page_url);
                setTo(response.getData()?.to);
                setTotal(response.getData()?.total || 0);
            }
        } finally {
            setLoading(false);
        }
    };

    // create a function to fetchKpi using activityId
    const fetchKpiByActivityId = async (activityId: number): Promise<Kpi[]> => {
        const response = await fetchAssignedKpiAsync(params.nationId, activityId, {
            all: true,
        });

        return response.getData()?.data || [];
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [
        params.nationId, params.page, params.all, params.freetext,
        params.activityId, params.kpiName, params.activityName,
        params.description, params.dataTypeName, params.unitName,
        params.kpiId, params.dataTypeId, params.unitId,
        params.isActive, params.entryMode,
        options.enabled,
    ]);

    return {
        current_page,
        data,
        first_page_url,
        from,
        last_page,
        last_page_url,
        links,
        next_page_url,
        path,
        per_page,
        prev_page_url,
        to,
        total,
        loading,
        reload,
        fetchKpiByActivityId,
    };
}